/**
 * Se souvenir qu'un matériel a déjà été vérifié — et savoir quand ce souvenir
 * ne vaut plus rien.
 *
 * Le principe : rien ne se dégrade avec le temps, c'est la **configuration**
 * qui bouge. On ne mémorise donc pas « vérifié il y a n jours » mais l'état des
 * lieux — plateforme, navigateur ou version d'app, permissions, périphériques
 * branchés — et on redemande à l'élève dès que cet état diffère.
 *
 * Fichier volontairement PUR : aucun import, aucune dépendance React. Il se
 * teste sans monter de composant, comme les autres règles du dossier.
 */

/**
 * Le filet, pour la dérive qu'aucune empreinte ne peut voir.
 *
 * L'empreinte est un indice, pas la vérité : un casque peut mourir en restant
 * branché, un pilote se mettre à jour, le système couper le micro — rien de
 * tout cela ne bouge dans la configuration. Sans échéance, un élève pourrait
 * ne jamais revérifier.
 */
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * L'état des lieux, réduit à une chaîne comparable.
 *
 * Tout ce qui entre ici doit être lisible **en silence** : ni fenêtre
 * d'autorisation, ni flux ouvert. C'est la condition pour que la vérification
 * rapide reste invisible.
 *
 * Côté matériel, l'empreinte ne retient que **l'appareil qui servira** — le
 * micro et la caméra par défaut — et non la liste de tout ce qui est branché.
 * Une première version prenait la liste entière, et elle bougeait sans que
 * rien d'utile ait changé : des écouteurs Bluetooth qui se connectent, un
 * iPhone qui apparaît comme caméra dès qu'il est à côté du Mac, un écran
 * externe, une caméra virtuelle. Chaque variation renvoyait au parcours
 * complet un élève dont le micro et la caméra étaient exactement ceux de la
 * veille. Ce qui compte est la question « vais-je parler dans le même micro
 * qu'hier ? » — et c'est celle-là que `deviceInUse` pose.
 *
 * La version du navigateur ou de l'app n'y entre plus (retiré — P-3925,
 * retour de Nada/Aziz/Wajih : une mise à jour ne doit pas faire rejouer tout
 * le parcours). Une mise à jour PEUT réinitialiser une permission, mais ce
 * risque est déjà couvert ailleurs, plus précisément : `read()`
 * (`useRememberedCheck`) exige `microphone`/`camera` à `"granted"` pour même
 * calculer une empreinte, relu EN SILENCE à chaque entrée. Si une mise à jour
 * a vraiment retiré l'accès, cette lecture échoue et le parcours complet
 * revient de toute façon — sans qu'il soit besoin d'invalider sur la seule
 * foi d'un numéro de version qui, neuf fois sur dix, n'a rien cassé.
 *
 * @param {object} state
 * @param {string} state.platform      web, ios, android
 * @param {string} state.microphone    état de la permission
 * @param {string} state.camera        état de la permission
 * @param {string} state.audioInput    identité du micro par défaut — web seulement
 * @param {string} state.videoInput    identité de la caméra par défaut — web seulement
 */
export function configSignature({
    platform = "",
    microphone = "",
    camera = "",
    audioInput = "",
    videoInput = "",
} = {}) {
    return digest([platform, microphone, camera, `${audioInput}/${videoInput}`].join("|"))
}

/**
 * La même empreinte, éclatée en trois morceaux comparables un par un.
 *
 * `configSignature` reste un bloc unique à partir de maintenant — toucher à
 * nouveau sa formule invaliderait tous les souvenirs déjà écrits, le même
 * coût (un parcours complet pour tout le monde une fois) que cette PR vient
 * justement de payer une fois, consciemment, en en retirant la version. On
 * ne veut pas le repayer à chaque fois qu'un diagnostic a besoin d'un détail
 * de plus. Mais un bloc unique ne dit jamais LEQUEL des trois a bougé quand
 * le raccourci échoue, et deviner à l'aveugle (casque Bluetooth ? caméra ?
 * mise à jour du navigateur ?) coûte cher avant de corriger. Trois condensats
 * séparés répondent à la question sans rien révéler de plus que
 * `configSignature` ne révèle déjà — jamais de libellé ni d'identifiant en
 * clair, juste de quoi comparer deux lectures.
 *
 * `browser` reste ici à titre purement diagnostique, même si
 * `configSignature` ne le regarde plus pour décider du raccourci : savoir
 * qu'une mise à jour a coïncidé avec un souci reste utile à lire dans
 * `memory_miss_detail`, seulement ça ne bloque plus personne tout seul.
 */
export function componentSignatures({
    platform = "",
    build = "",
    microphone = "",
    camera = "",
    audioInput = "",
    videoInput = "",
} = {}) {
    return {
        browser: digest([platform, build].join("|")),
        microphone: digest([microphone, audioInput].join("|")),
        camera: digest([camera, videoInput].join("|")),
    }
}

/**
 * L'appareil que le navigateur ouvrira si on ne lui en désigne aucun.
 *
 * Chrome liste un pseudo-périphérique `default` dont l'identifiant ne change
 * jamais, quel que soit l'appareil réel derrière : brancher un casque ne le
 * fait pas bouger. C'est son libellé — « Default - Casque » — qui dit ce qui a
 * changé. Ailleurs, le premier de la liste est celui que `getUserMedia`
 * choisit. Le libellé est préféré à l'identifiant partout : il ne dépend pas
 * du sel que le navigateur applique aux identifiants et qu'il peut renouveler,
 * et il n'est lisible qu'une fois l'accès accordé — ce que le raccourci exige
 * déjà. Aucun appareil : chaîne vide, pour que « rien de branché » reste
 * distinct de « quelque chose de branché ».
 *
 * Un appareil choisi à la main gagne sur le défaut : c'est dans celui-là que
 * l'élève vient de parler. Sans ça, l'élève dont le micro intégré est mort
 * trouvait son micro USB par le dépannage, terminait au vert, et l'empreinte
 * enregistrait le micro intégré — celui-là même qu'il avait dû abandonner. La
 * visite suivante retrouvait ce micro intact, sautait le test, et la salle
 * rouvrait le micro mort. Le choix, lui, ne survit pas à la séance
 * (`precallDevices` est effacé à la sortie), donc la lecture suivante retombe
 * sur le défaut, ne correspond plus, et le test est rejoué — ce qui est
 * exactement ce qu'il faut.
 *
 * Aucune caméra n'a jamais de `default` — aucun OS n'a de notion de « caméra
 * par défaut », contrairement au micro. Le repli retombait donc sur
 * `devices[0]`, le premier de la liste rendue par `enumerateDevices()` — un
 * ordre que la spec ne garantit PAS stable d'un appel à l'autre, même quand le
 * matériel n'a pas bougé d'un poil (confirmé : même navigateur, même profil,
 * deux lectures consécutives, deux ordres différents). C'était la cause
 * prouvée des `memory_miss: "changed"` à répétition sur Mac (P-3925) : l'ordre
 * change, pas la caméra. Trier avant de prendre « le premier » rend le choix
 * indépendant de cet ordre — la même caméra gagne qu'elle soit lue en
 * première ou en dernière position.
 *
 * @param {{deviceId?: string, label?: string}[]} devices  d'une seule sorte
 * @param {string|null} chosenId  l'appareil désigné par l'élève, s'il l'a fait
 */
/**
 * Et pour le micro, sur Safari/Firefox/natif, où `default` n'existe pas non
 * plus (seul Chrome le synthétise) ? Même tri, par construction — la fonction
 * ne distingue pas micro et caméra, et ne doit pas : un micro qui apparaît
 * sans devenir le choix réel du système (casque Bluetooth qui s'appaire,
 * caméra externe qui se branche) ne doit justement PAS faire bouger
 * l'empreinte — c'est déjà la règle documentée (`docs/troubleshooting-
 * live-sessions.md`, « et ces cas ne comptent pas »). Un tri déterministe ne
 * dégrade donc rien par rapport à AVANT cette PR : sans signal système d'un
 * côté comme de l'autre, aucune des deux versions ne pouvait garantir de
 * suivre le changement réel — l'ancienne version l'aurait ratée par ordre
 * instable, celle-ci le rate par choix stable mais possiblement périmé. Le
 * filet des 30 jours (`MAX_AGE_MS`) couvre ce résidu, comme il couvre déjà le
 * casque qui meurt sans se débrancher.
 */
export function deviceInUse(devices = [], chosenId = null) {
    if (!devices.length) return ""
    // Un appareil débranché entre-temps n'est plus dans la liste : on retombe
    // sur le défaut plutôt que de rendre une chaîne vide.
    const chosen = chosenId ? devices.find((d) => d.deviceId === chosenId) : null
    const stable = [...devices].sort((a, b) =>
        (a.label || a.deviceId).localeCompare(b.label || b.deviceId)
    )
    const preferred = chosen ?? devices.find((d) => d.deviceId === "default") ?? stable[0]
    return preferred.label || preferred.deviceId || ""
}

/**
 * Des libellés renseignés valent une permission accordée.
 *
 * Un navigateur ne nomme un périphérique qu'à qui en a obtenu l'accès : lire
 * « FaceTime HD Camera » plutôt qu'une chaîne vide, c'est savoir l'accès
 * donné, sans rien ouvrir. Le souvenir du test s'en sert quand
 * `permissions.query` ne répond pas ; le live, pour savoir s'il peut rouvrir
 * le micro sans reposer la question.
 *
 * Aucun périphérique du tout : on ne conclut rien — un ordinateur sans caméra
 * n'a pas refusé la caméra, et « inconnu » renverra au parcours complet, ce
 * qui est le bon défaut.
 *
 * @param {{label?: string}[]} devices  d'une seule sorte
 */
export function grantedFromLabels(devices = []) {
    if (!devices.length) return "inconnu"
    return devices.some((d) => d.label) ? "granted" : "inconnu"
}

/**
 * L'accès à un appareil, lu sans rien ouvrir : ce que dit `permissions.query`,
 * et les noms des appareils seulement quand il ne répond pas.
 *
 * L'ordre compte : un nom ne prouve pas l'accès à CET appareil. WebKit nomme
 * tous les périphériques dès qu'un accès quelconque est acquis (la caméra
 * accordée, le micro encore « à demander ») ; un « prompt » explicite l'emporte
 * donc sur des noms visibles. Le souvenir du test et le live (le micro qu'il
 * peut rouvrir sans geste) lisent cette même règle.
 *
 * @param {string|null} permission  l'état lu (`granted`, `denied`, `prompt`), `null` s'il ne se lit pas
 * @param {{label?: string}[]} devices  ceux de la même sorte, lus seulement quand `permission` est `null`
 */
export function accessOf(permission, devices = []) {
    return permission ?? grantedFromLabels(devices)
}

/**
 * On ne conserve qu'une empreinte, jamais l'état des lieux lui-même.
 *
 * Ce module n'a besoin que d'une chose : savoir si deux configurations sont
 * identiques. Il n'a jamais à relire ce qu'elles contenaient. Or ce contenu
 * comprend les identifiants de périphérique du navigateur — les mêmes valeurs
 * que `precallDevices`, que l'on prend soin d'effacer à la déconnexion parce
 * qu'elles traversent la frontière entre deux comptes d'une même machine.
 * Les garder en clair ici aurait rouvert la question pour rien.
 *
 * Un condensat suffit donc, et il ferme le sujet : ce qui est stocké ne se
 * relit pas, ne s'applique pas, et ne décrit plus rien de lisible.
 *
 * Le condensat n'a pas à être cryptographique — on compare les quelques
 * configurations successives d'un même poste, pas des empreintes venues de
 * l'extérieur. Il doit en revanche être STABLE d'une version de l'app à
 * l'autre : le changer invaliderait tous les souvenirs enregistrés, et
 * renverrait chaque élève au parcours complet.
 */
function digest(text) {
    let h1 = 0xdeadbeef
    let h2 = 0x41c6ce57
    for (let i = 0; i < text.length; i += 1) {
        const ch = text.charCodeAt(i)
        h1 = Math.imul(h1 ^ ch, 2654435761)
        h2 = Math.imul(h2 ^ ch, 1597334677)
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)
}

/** Pourquoi le parcours complet est rejoué malgré tout. */
export const MISS = {
    /** Aucun souvenir sur cet appareil. */
    NONE: "none",
    /** La configuration n'est plus celle du souvenir. */
    CHANGED: "changed",
    /** Le souvenir a dépassé le filet de trente jours. */
    EXPIRED: "expired",
    /**
     * Un souvenir existe, mais la configuration du jour ne se lit pas — un
     * accès retiré, un navigateur qui ne répond ni sur les permissions ni sur
     * les libellés.
     *
     * Distinct de `NONE`, et la distinction est le but de la mesure : sans
     * souvenir, l'élève fera le raccourci la prochaine fois ; avec un souvenir
     * illisible, il ne le fera jamais. Les confondre laisserait une population
     * définitivement privée du chemin court invisible dans les chiffres.
     */
    UNREADABLE: "unreadable",
}

/**
 * Ce qui empêche de faire l'économie du parcours complet — `null` si rien.
 *
 * Trois conditions, et il faut les trois : un souvenir existe, la
 * configuration n'a pas bougé, et le souvenir n'est pas trop vieux. La raison
 * du refus est rendue, pas seulement le refus : un élève qui vient de faire le
 * test et le retrouve sans un mot d'explication conclut que l'écran est cassé.
 *
 * Une empreinte du jour illisible est traitée ici, et non chez l'appelant :
 * tout ce qui invalide le souvenir se lit dans ce fichier, sinon la règle
 * suivante sera ajoutée à celui des deux endroits que le contributeur aura
 * sous les yeux.
 *
 * @param {{signature: string, at: number, components?: object}|null} remembered
 * @param {string|null} current  l'empreinte lue à l'instant, vide si illisible
 * @param {number} now
 * @returns {string|null} une valeur de `MISS`, ou `null` quand le raccourci s'applique
 */
export function whyNotSkip(remembered, current, now = 0) {
    if (!remembered?.signature || !remembered?.at) return MISS.NONE
    if (!current) return MISS.UNREADABLE
    if (remembered.signature !== current) return MISS.CHANGED
    if (now - remembered.at >= MAX_AGE_MS) return MISS.EXPIRED
    return null
}

/**
 * Quels morceaux de l'empreinte ont bougé — pour le diagnostic seulement,
 * jamais pour décider du raccourci : `whyNotSkip`, sur le bloc unique, reste
 * la seule autorité sur ce qui est mémorisable ou non.
 *
 * Un souvenir écrit avant l'ajout de ce détail n'a pas de `components` : la
 * réponse est alors une liste vide plutôt qu'une fausse certitude — elle se
 * résorbe d'elle-même trente jours après le déploiement, le temps que le
 * filet expire les derniers souvenirs sans détail.
 *
 * @param {{components?: {browser: string, microphone: string, camera: string}}|null} remembered
 * @param {{browser: string, microphone: string, camera: string}|null} current
 * @returns {string[]} sous-ensemble de `["browser", "microphone", "camera"]`
 */
export function whichChanged(remembered, current) {
    if (!remembered?.components || !current) return []
    return ["browser", "microphone", "camera"].filter(
        (part) => remembered.components[part] !== current[part]
    )
}

/**
 * Le navigateur, réduit à ce qui compte : son nom et sa version majeure.
 *
 * Une mise à jour majeure peut réinitialiser des permissions ; un correctif de
 * sécurité, non. Garder la version complète ferait donc rejouer le parcours
 * pour rien, plusieurs fois par mois.
 */
export function browserKey(userAgent = "") {
    const match = userAgent.match(/(Firefox|Edg|OPR|Chrome|Safari)\/(\d+)/)
    return match ? `${match[1]}${match[2]}` : "inconnu"
}

/**
 * Quel navigateur, en une famille — pour le guidage, pas pour l'empreinte.
 *
 * Les moteurs Chromium (Chrome, Edge, Opera) affichent la même chose au même
 * endroit ; Firefox et Safari, non. C'est cette distinction-là qui compte pour
 * dire à l'élève où regarder, et elle est plus grossière que `browserKey`.
 */
export function browserFamily(userAgent = "") {
    const ua = userAgent.toLowerCase()
    if (ua.includes("firefox")) return "firefox"
    if (ua.includes("edg")) return "edge"
    // Chrome doit passer APRÈS Edge et Opera, qui portent tous deux « chrome »
    // dans leur signature.
    if (ua.includes("opr") || ua.includes("chrome")) return "chrome"
    if (ua.includes("safari")) return "safari"
    return "autre"
}

/**
 * Sur téléphone, il n'existe pas de liste d'identifiants de périphérique : on
 * ne verra donc pas qu'un casque a été branché ou débranché. Ce qui reste
 * observable, c'est la version du système et celle de l'app — l'une comme
 * l'autre peuvent réinitialiser des permissions.
 *
 * Ce trou est assumé, parce que le risque n'est pas le même qu'au bureau : un
 * téléphone a toujours son micro et son haut-parleur intégrés, et ils
 * fonctionnent. La panne typique sur mobile est l'autorisation retirée — et
 * celle-là, l'empreinte la voit.
 */
export function nativeBuildKey({ osVersion = "", appVersion = "" } = {}) {
    return `os${osVersion || "?"}+app${appVersion || "?"}`
}
